/// Phase 2: developer-reply inbox.
///
/// Each time the developer comments on someone else's post on the
/// Talk-to-the-developer board, the `onDevCommentWrite` trigger
/// (admin SDK) writes a doc at
/// `webUsers/{recipientUid}/inbox/{commentId}`. This module is the
/// client-side reader + mark-read helper.
///
/// Visibility model:
///   - Each user can read + update their own inbox subcollection
///     (rules enforce `request.auth.uid == uid`).
///   - Updates are restricted to flipping `readAt` from null to a
///     server timestamp — the trigger owns every other field.
///   - Deletes are allowed (so a user can clear stale entries
///     pointing at deleted posts).
///   - Creates by clients are forbidden — server-only via the
///     comment trigger.
///
/// The data shape is symmetric across web and iOS (Phase 3): iOS
/// will subscribe to the same collection and use the same fields.
/// Keep this in sync with the trigger in
/// `functions/src/devBlog.ts:writeInboxNotificationOnReply` if you
/// touch either side.

import {
  collection,
  deleteDoc,
  doc,
  limit as limitTo,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type FirestoreError,
  type Timestamp,
} from 'firebase/firestore'
import { firestore } from '../firebase/app'

export interface InboxItem {
  id: string
  /// The comment id on the Talk-to-the-developer board. Doubles as
  /// the doc id under `webUsers/{uid}/inbox/`.
  commentId: string
  postId: string
  /// Snapshotted at write time so the inbox entry stays readable
  /// even if the post is later renamed or archived. Empty string
  /// when the post had no title (e.g. feedback posts may have an
  /// auto-synthesized title with stars + level id).
  postTitle: string
  postKind: 'feedback' | 'discussion'
  /// Display name for the replier. The server snapshots the dev's
  /// displayName at write time, falling back to "Developer" when
  /// the dev's name field was empty.
  replierName: string
  /// First ~120 chars of the reply body, single-lined.
  preview: string
  createdAt: Timestamp | null
  /// `null` until the recipient marks the entry read. The home-
  /// screen badge / banner only surfaces unread entries; once
  /// read, the doc lingers as a small history trail (no automatic
  /// purge in Phase 2).
  readAt: Timestamp | null
}

function inboxCol(uid: string) {
  return collection(firestore, 'webUsers', uid, 'inbox')
}

function inboxDoc(uid: string, notifId: string) {
  return doc(firestore, 'webUsers', uid, 'inbox', notifId)
}

function mapItem(id: string, data: Record<string, unknown>): InboxItem {
  const kind = data.postKind === 'discussion' ? 'discussion' : 'feedback'
  return {
    id,
    commentId: typeof data.commentId === 'string' ? data.commentId : id,
    postId: typeof data.postId === 'string' ? data.postId : '',
    postTitle: typeof data.postTitle === 'string' ? data.postTitle : '',
    postKind: kind,
    replierName: typeof data.replierName === 'string' ? data.replierName : 'Developer',
    preview: typeof data.preview === 'string' ? data.preview : '',
    createdAt: (data.createdAt as Timestamp | null) ?? null,
    readAt: (data.readAt as Timestamp | null) ?? null,
  }
}

/// Subscribe to the recent inbox, newest first. Capped at `limit`
/// (default 50) — the badge + banner only need the top few; the
/// rest is a history trail we don't surface yet. Returns the
/// unsubscribe.
export function subscribeInbox(args: {
  uid: string
  limit?: number
  onChange: (items: InboxItem[]) => void
  onError?: (err: FirestoreError) => void
}): () => void {
  const q = query(inboxCol(args.uid), orderBy('createdAt', 'desc'), limitTo(args.limit ?? 50))
  return onSnapshot(
    q,
    (snap) => {
      const out: InboxItem[] = []
      snap.forEach((d) => out.push(mapItem(d.id, d.data({ serverTimestamps: 'estimate' }))))
      args.onChange(out)
    },
    (err) => args.onError?.(err),
  )
}

/// Subscribe to *unread* inbox items only. Drives the home-screen
/// badge count + the first-unread banner copy. Returns unsubscribe.
export function subscribeUnreadInbox(args: {
  uid: string
  onChange: (items: InboxItem[]) => void
  onError?: (err: FirestoreError) => void
}): () => void {
  // `where readAt == null` + `orderBy createdAt desc` is a
  // composite query — Firestore needs an index. The cheapest path
  // is to skip the orderBy on the server (single-field equality
  // doesn't need a composite index) and sort client-side. The
  // inbox is always small (a handful of unread per user), so the
  // client-side sort is negligible.
  const q = query(inboxCol(args.uid), where('readAt', '==', null))
  return onSnapshot(
    q,
    (snap) => {
      const out: InboxItem[] = []
      snap.forEach((d) => out.push(mapItem(d.id, d.data({ serverTimestamps: 'estimate' }))))
      out.sort((a, b) => {
        const am = a.createdAt?.toMillis() ?? 0
        const bm = b.createdAt?.toMillis() ?? 0
        return bm - am
      })
      args.onChange(out)
    },
    (err) => args.onError?.(err),
  )
}

/// Stamp `readAt = serverTimestamp` on every unread inbox doc
/// pointing at `postId`. Called when the user opens the post
/// detail (or dismisses the home-screen banner for a specific
/// thread). Idempotent — a no-op when there's nothing unread.
export async function markInboxReadForPost(args: {
  uid: string
  postId: string
  items: readonly InboxItem[]
}): Promise<void> {
  const targets = args.items.filter((it) => it.postId === args.postId && it.readAt === null)
  if (targets.length === 0) return
  const batch = writeBatch(firestore)
  for (const it of targets) {
    batch.update(inboxDoc(args.uid, it.id), { readAt: serverTimestamp() })
  }
  await batch.commit()
}

/// One-shot mark-read for a single inbox doc. Useful when the
/// home-screen banner has a "Dismiss" button — we don't want to
/// open the post, just clear that one entry.
export async function markInboxItemRead(args: {
  uid: string
  notifId: string
}): Promise<void> {
  await updateDoc(inboxDoc(args.uid, args.notifId), { readAt: serverTimestamp() })
}

/// Hard-delete an inbox doc. Surfaced as a "delete" option on
/// stale entries (e.g. the post they point at has been removed by
/// the developer). Rules allow self-delete on `inbox` so this
/// works without a server hop.
export async function deleteInboxItem(args: { uid: string; notifId: string }): Promise<void> {
  await deleteDoc(inboxDoc(args.uid, args.notifId))
}
